import invariant from 'invariant';
import { useEffect, useRef, useState } from 'react';

import { useTabContext } from '#/containers/TabContext';
import { waitForElement } from '#/utils/waitForElement';

import { modalComponentByType } from './modalComponentByType';
import type { ModalPropsForType } from './modalComponentByType';
import type { ModalType } from './ModalType';

type Props<T extends ModalType> = {
  type: ModalType;
  modalProps: ModalPropsForType<T>;
};

export function ModalByType<T extends ModalType>({
  type,
  modalProps,
}: Props<T>) {
  const [modalContext, setModalContext] = useState<HTMLElement | null>(null);
  const [focused, setFocused] = useState(false);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const isTabbing = useTabContext();

  useEffect(() => {
    if (focused) return;

    setFocused(true);

    const modalContextElement = document.querySelector<HTMLElement>('*:focus');
    setModalContext(modalContextElement);

    waitForElement(() => {
      if (modalContainerRef?.current) {
        const container = modalContainerRef.current;
        const els = Array.from(
          container.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (isTabbing) {
          return (els.find((el) =>
            el.matches(':not([data-modal-focus="false"])'),
          ) ?? els[0]) as HTMLElement | null | undefined;
        }

        const firstFocusableElement =
          els.find((el) => el.matches('[data-modal-focus="true"]')) ??
          els.find((el) => el.matches(':not([data-modal-focus="false"])')) ??
          els[0];
        return firstFocusableElement as HTMLElement | null | undefined;
      }
    }, 200)
      .then((el) => {
        el.focus();

        return undefined;
      })
      .catch(() => {
        // do nothing
      });
  }, [type, modalContext, isTabbing, focused]);

  const ModalComponent = modalComponentByType[type];
  invariant(ModalComponent, `Invalid modal type: ${type}`);

  return (
    <div ref={modalContainerRef}>
      {/* @ts-expect-error TS(2604): JSX element type 'ModalComponent' does not have an... Remove this comment to see the full error message */}
      <ModalComponent
        // @ts-expect-error not much we can do about this
        // We need to spread here, no other choice unless we stop showing modals
        // using Redux
        //
        // oxlint-disable-next-line react/jsx-props-no-spreading
        {...modalProps}
      />
    </div>
  );
}
